import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import {
  fixedAssetsApi,
  MODE_LABEL,
  type AssetCategory,
  type AssetInput,
  type CapexRequest,
  type CapitalizationMode,
  type DepreciationMethod,
  type GstType,
} from '@/api/fixedAssetsApi';
import { bankReconApi, type CashBankLedger } from '@/api/bankReconApi';
import { getSuppliers, type PartyResponse } from '@/api/party.api';
import { getAllWarehouses } from '@/api/warehouse.api';
import { EntryPreview, Field, GstFields, Section, errMsg, inputCls, money, primaryBtn, secondaryBtn, todayStr } from '../components/ui';

type Form = {
  assetName: string;
  assetCode: string;
  categoryId: string;
  description: string;
  serialNumber: string;
  makeModel: string;
  capitalizationMode: CapitalizationMode;
  purchaseDate: string;
  depreciationStartDate: string;
  paymentAccountId: string;
  vendorId: string;
  purchaseInvoiceId: string;
  purchaseCost: number;
  additionalCost: number;
  gstType: GstType;
  gstAmount: number;
  itcEligible: boolean;
  depreciationMethod: DepreciationMethod;
  depreciationRate: number;
  usefulLifeYears: number | '';
  scrapValue: number;
  openingAccumulatedDepreciation: number;
  openingDepreciationUpto: string;
  warehouseId: string;
  department: string;
  locationDetail: string;
  custodianName: string;
  machineId: string;
  insuranceCompany: string;
  insurancePolicyNo: string;
  insuranceExpiry: string;
  insuredValue: number;
  warrantyExpiry: string;
  capexRequestId: string;
};

const empty: Form = {
  assetName: '', assetCode: '', categoryId: '', description: '', serialNumber: '', makeModel: '',
  capitalizationMode: 'BANK', purchaseDate: todayStr(), depreciationStartDate: '', paymentAccountId: '', vendorId: '', purchaseInvoiceId: '',
  purchaseCost: 0, additionalCost: 0, gstType: 'NONE', gstAmount: 0, itcEligible: true,
  depreciationMethod: 'SLM', depreciationRate: 0, usefulLifeYears: '', scrapValue: 0,
  openingAccumulatedDepreciation: 0, openingDepreciationUpto: '',
  warehouseId: '', department: '', locationDetail: '', custodianName: '', machineId: '',
  insuranceCompany: '', insurancePolicyNo: '', insuranceExpiry: '', insuredValue: 0, warrantyExpiry: '', capexRequestId: '',
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export default function NewAssetPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const capexParam = params.get('capex');

  const [form, setForm] = useState<Form>(empty);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [ledgers, setLedgers] = useState<CashBankLedger[]>([]);
  const [suppliers, setSuppliers] = useState<PartyResponse[]>([]);
  const [capex, setCapex] = useState<CapexRequest[]>([]);
  const [invoices, setInvoices] = useState<Awaited<ReturnType<typeof fixedAssetsApi.getEligibleInvoices>>>([]);
  const [machines, setMachines] = useState<Awaited<ReturnType<typeof fixedAssetsApi.getMachines>>>([]);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [financialLocked, setFinancialLocked] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (patch: Partial<Form>) => setForm((p) => ({ ...p, ...patch }));

  useEffect(() => {
    (async () => {
      try {
        const [cats, cb, sup, cx, inv, mc, wh] = await Promise.all([
          fixedAssetsApi.getCategories(),
          bankReconApi.getCashBankLedgers().catch(() => []),
          getSuppliers().then((r) => r.data || []).catch(() => []),
          fixedAssetsApi.getCapexRequests().catch(() => []),
          fixedAssetsApi.getEligibleInvoices().catch(() => []),
          fixedAssetsApi.getMachines().catch(() => []),
          getAllWarehouses().then((r: any) => r.data || []).catch(() => []),
        ]);
        setCategories(cats);
        setLedgers(cb);
        setSuppliers(sup);
        setCapex(cx.filter((c) => c.status === 'APPROVED'));
        setInvoices(inv);
        setMachines(mc);
        setWarehouses(wh.map((w: any) => ({ id: w.id, name: w.name })));

        if (editId) {
          const d = await fixedAssetsApi.getAssetById(editId);
          const a = d.asset;
          setFinancialLocked(d.depreciationHistory.length > 0 || d.maintenance.some((m) => m.maintenance_type === 'CAPITAL'));
          setForm({
            ...empty,
            assetName: a.asset_name, assetCode: a.asset_code, categoryId: a.category_id, description: a.description || '',
            serialNumber: a.serial_number || '', makeModel: a.make_model || '', capitalizationMode: a.capitalization_mode,
            purchaseDate: a.purchase_date, depreciationStartDate: a.depreciation_start_date, paymentAccountId: a.payment_account_id || '',
            vendorId: a.vendor_id || '', purchaseInvoiceId: a.purchase_invoice_id || '',
            purchaseCost: a.purchase_cost, additionalCost: a.additional_cost, gstType: a.gst_type, gstAmount: a.gst_paid, itcEligible: a.itc_eligible,
            depreciationMethod: a.depreciation_method, depreciationRate: a.depreciation_rate, usefulLifeYears: a.useful_life_years ?? '',
            scrapValue: a.scrap_value, openingAccumulatedDepreciation: a.opening_accumulated_depreciation,
            openingDepreciationUpto: a.capitalization_mode === 'OPENING' ? a.depreciated_upto || '' : '',
            warehouseId: a.warehouse_id || '', department: a.department || '', locationDetail: a.location_detail || '',
            custodianName: a.custodian_name || '', machineId: a.machine_id || '',
            insuranceCompany: a.insurance_company || '', insurancePolicyNo: a.insurance_policy_no || '', insuranceExpiry: a.insurance_expiry || '',
            insuredValue: a.insured_value || 0, warrantyExpiry: a.warranty_expiry || '', capexRequestId: a.capex_request_id || '',
          });
          if (a.purchase_invoice_id && !inv.some((i) => i.id === a.purchase_invoice_id)) {
            setInvoices((p) => [{ id: a.purchase_invoice_id!, invoice_number: a.purchase_invoice_number || 'Linked invoice', invoice_date: a.purchase_date, taxable_amount: a.purchase_cost, total_amount: a.purchase_cost, supplier_name: a.vendor_name }, ...p]);
          }
        } else {
          const req = capexParam ? cx.find((c) => c.id === capexParam && c.status === 'APPROVED') : undefined;
          const cat = (req?.category_id && cats.find((c) => c.id === req.category_id)) || cats[0];
          if (cat) applyCategory(cat, req ? { assetName: req.asset_name, purchaseCost: req.estimated_cost, department: req.department || '', capexRequestId: req.id, usefulLifeYears: req.expected_life_years || '' } : {});
        }
      } catch (err) {
        toast.error(errMsg(err, 'Failed to load the form'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const applyCategory = (cat: AssetCategory, extra: Partial<Form> = {}) => {
    setForm((p) => ({
      ...p,
      categoryId: cat.id,
      depreciationMethod: cat.depreciation_method,
      depreciationRate: cat.book_depreciation_rate,
      usefulLifeYears: cat.useful_life_years ?? '',
      ...extra,
    }));
  };

  const category = categories.find((c) => c.id === form.categoryId);
  const gst = form.gstType === 'NONE' ? 0 : Number(form.gstAmount || 0);
  const isOpening = form.capitalizationMode === 'OPENING';
  const isInvoice = form.capitalizationMode === 'PURCHASE_INVOICE';
  const capitalized = round2(Number(form.purchaseCost || 0) + Number(form.additionalCost || 0) + (!isOpening && !isInvoice && !form.itcEligible ? gst : 0));
  const paidTotal = round2(Number(form.purchaseCost || 0) + Number(form.additionalCost || 0) + (isOpening || isInvoice ? 0 : gst));

  const schedule = useMemo(() => {
    const rows: { year: number; opening: number; dep: number; closing: number }[] = [];
    let bv = round2(capitalized - (isOpening ? Number(form.openingAccumulatedDepreciation || 0) : 0));
    const scrap = Number(form.scrapValue || 0);
    if (form.depreciationMethod === 'NONE' || bv <= 0) return rows;
    const life = Number(form.usefulLifeYears || 0);
    for (let y = 1; y <= 6 && bv > scrap + 0.5; y++) {
      let dep = form.depreciationMethod === 'WDV' ? (bv * Number(form.depreciationRate || 0)) / 100 : life > 0 ? (capitalized - scrap) / life : 0;
      dep = Math.min(round2(dep), round2(bv - scrap));
      if (dep <= 0) break;
      rows.push({ year: y, opening: bv, dep, closing: round2(bv - dep) });
      bv = round2(bv - dep);
    }
    return rows;
  }, [capitalized, form.depreciationMethod, form.depreciationRate, form.usefulLifeYears, form.scrapValue, form.openingAccumulatedDepreciation, isOpening]);

  const assetLedger = category?.asset_acc_name ? `${category.asset_acc_code} — ${category.asset_acc_name}` : 'Asset ledger (set on category)';
  const creditLabel = {
    BANK: ledgers.find((l) => l.id === form.paymentAccountId)?.name || 'Default bank account',
    CASH: ledgers.find((l) => l.id === form.paymentAccountId)?.name || 'Cash in Hand',
    VENDOR_CREDIT: `Accounts Payable — ${suppliers.find((s) => s.id === form.vendorId)?.name || 'vendor'}`,
    PURCHASE_INVOICE: 'Purchases (reclassified from the bill)',
    OPENING: 'Opening Balance Equity',
  }[form.capitalizationMode];
  const entry = isOpening
    ? [
      { side: 'DR' as const, account: assetLedger, amount: capitalized },
      { side: 'CR' as const, account: 'Accumulated Depreciation', amount: Number(form.openingAccumulatedDepreciation || 0) },
      { side: 'CR' as const, account: creditLabel, amount: round2(capitalized - Number(form.openingAccumulatedDepreciation || 0)) },
    ]
    : isInvoice
      ? [
        { side: 'DR' as const, account: assetLedger, amount: capitalized },
        { side: 'CR' as const, account: creditLabel, amount: capitalized },
      ]
      : [
        { side: 'DR' as const, account: assetLedger, amount: capitalized },
        ...(form.itcEligible && gst ? [{ side: 'DR' as const, account: form.gstType === 'IGST' ? 'Input IGST' : 'Input CGST + SGST', amount: gst }] : []),
        { side: 'CR' as const, account: creditLabel, amount: paidTotal },
      ];

  const handleSave = async () => {
    if (!form.assetName.trim()) return toast.error('Asset name is required');
    if (!form.categoryId) return toast.error('Select a category');
    if (!(form.purchaseCost > 0)) return toast.error('Enter the purchase cost');
    if (form.capitalizationMode === 'VENDOR_CREDIT' && !form.vendorId) return toast.error('Select the vendor');
    if (form.capitalizationMode === 'PURCHASE_INVOICE' && !form.purchaseInvoiceId) return toast.error('Select the purchase invoice');
    const payload: AssetInput = {
      assetName: form.assetName.trim(),
      assetCode: form.assetCode.trim() || undefined,
      categoryId: form.categoryId,
      description: form.description || undefined,
      serialNumber: form.serialNumber || undefined,
      makeModel: form.makeModel || undefined,
      capitalizationMode: form.capitalizationMode,
      purchaseDate: form.purchaseDate,
      depreciationStartDate: form.depreciationStartDate || form.purchaseDate,
      paymentAccountId: ['BANK', 'CASH'].includes(form.capitalizationMode) ? form.paymentAccountId || undefined : undefined,
      vendorId: form.vendorId || undefined,
      purchaseInvoiceId: isInvoice ? form.purchaseInvoiceId : undefined,
      purchaseCost: Number(form.purchaseCost),
      additionalCost: Number(form.additionalCost || 0),
      gstType: isOpening || isInvoice ? 'NONE' : form.gstType,
      gstAmount: isOpening || isInvoice ? 0 : gst,
      itcEligible: form.itcEligible,
      scrapValue: Number(form.scrapValue || 0),
      depreciationMethod: form.depreciationMethod,
      depreciationRate: form.depreciationMethod === 'WDV' ? Number(form.depreciationRate) : undefined,
      usefulLifeYears: form.usefulLifeYears === '' ? null : Number(form.usefulLifeYears),
      openingAccumulatedDepreciation: isOpening ? Number(form.openingAccumulatedDepreciation || 0) : 0,
      openingDepreciationUpto: isOpening && form.openingDepreciationUpto ? form.openingDepreciationUpto : undefined,
      warehouseId: form.warehouseId || undefined,
      department: form.department || undefined,
      locationDetail: form.locationDetail || undefined,
      custodianName: form.custodianName || undefined,
      machineId: form.machineId || null,
      insuranceCompany: form.insuranceCompany || undefined,
      insurancePolicyNo: form.insurancePolicyNo || undefined,
      insuranceExpiry: form.insuranceExpiry || undefined,
      insuredValue: form.insuredValue ? Number(form.insuredValue) : undefined,
      warrantyExpiry: form.warrantyExpiry || undefined,
      capexRequestId: form.capexRequestId || undefined,
    };
    try {
      setSaving(true);
      if (editId) {
        const { assetCode, capexRequestId, ...rest } = payload;
        void assetCode;
        void capexRequestId;
        const body = financialLocked
          ? {
            assetName: rest.assetName, description: rest.description, serialNumber: rest.serialNumber, makeModel: rest.makeModel,
            warehouseId: rest.warehouseId, department: rest.department, locationDetail: rest.locationDetail, custodianName: rest.custodianName,
            machineId: rest.machineId, insuranceCompany: rest.insuranceCompany, insurancePolicyNo: rest.insurancePolicyNo,
            insuranceExpiry: rest.insuranceExpiry, insuredValue: rest.insuredValue, warrantyExpiry: rest.warrantyExpiry,
          }
          : rest;
        await fixedAssetsApi.updateAsset(editId, body);
        toast.success('Asset updated');
        navigate(`/finance/assets/${editId}`);
      } else {
        const res = await fixedAssetsApi.createAsset(payload);
        toast.success(res.message || 'Asset registered');
        navigate(`/finance/assets/${res.data.id}`);
      }
    } catch (err) {
      toast.error(errMsg(err, 'Failed to save the asset'));
    } finally {
      setSaving(false);
    }
  };

  const lockedNote = financialLocked ? 'Locked — depreciation or a capital improvement has been posted' : undefined;
  const bankLedgers = ledgers.filter((l) => (form.capitalizationMode === 'CASH' ? l.kind === 'CASH' : l.kind === 'BANK'));

  return (
    <AppLayout>
      <div className="p-6 max-w-[1100px] mx-auto space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">{editId ? 'Edit Fixed Asset' : 'Register Fixed Asset'}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {editId ? form.assetCode : 'Capitalise the asset — the purchase is posted to the General Ledger automatically'}
            </p>
          </div>
          <button onClick={() => navigate(editId ? `/finance/assets/${editId}` : '/finance/assets')} className={secondaryBtn}>
            <i className="ri-arrow-left-line" /> Back
          </button>
        </div>

        {!editId && capex.length > 0 && (
          <Section title="Approved CAPEX Request" subtitle="Link the purchase to the capital expenditure request it was approved under">
            <select
              value={form.capexRequestId}
              onChange={(e) => {
                const req = capex.find((c) => c.id === e.target.value);
                if (!req) return set({ capexRequestId: '' });
                const cat = categories.find((c) => c.id === req.category_id);
                if (cat) applyCategory(cat);
                set({ capexRequestId: req.id, assetName: form.assetName || req.asset_name, purchaseCost: form.purchaseCost || req.estimated_cost, department: form.department || req.department || '' });
              }}
              className={inputCls}
            >
              <option value="">— Not under a CAPEX request —</option>
              {capex.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.request_number} · {c.asset_name} · est. {money(c.estimated_cost)}
                </option>
              ))}
            </select>
          </Section>
        )}

        <Section title="Asset Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Asset Name" required className="lg:col-span-2">
              <input value={form.assetName} onChange={(e) => set({ assetName: e.target.value })} placeholder="e.g. CNC Machine" className={inputCls} />
            </Field>
            <Field label="Category" required hint={category ? `Tax (IT Act) rate ${category.it_depreciation_rate}% · ledger ${category.asset_acc_code || '—'}` : undefined}>
              <select
                value={form.categoryId}
                disabled={financialLocked}
                onChange={(e) => {
                  const cat = categories.find((c) => c.id === e.target.value);
                  if (cat) applyCategory(cat);
                }}
                className={inputCls}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Asset Code" hint={editId ? undefined : 'Leave blank to auto-generate'}>
              <input value={form.assetCode} disabled={!!editId} onChange={(e) => set({ assetCode: e.target.value })} placeholder="FA-MACH-0001" className={`${inputCls} font-mono`} />
            </Field>
            <Field label="Serial Number">
              <input value={form.serialNumber} onChange={(e) => set({ serialNumber: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Make / Model">
              <input value={form.makeModel} onChange={(e) => set({ makeModel: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Description" className="sm:col-span-2 lg:col-span-3">
              <input value={form.description} onChange={(e) => set({ description: e.target.value })} className={inputCls} />
            </Field>
          </div>
        </Section>

        <Section title="Purchase & Capitalisation" subtitle={lockedNote || 'Every cost needed to make the asset ready for use is capitalised'}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {(Object.keys(MODE_LABEL) as CapitalizationMode[]).map((m) => (
                <label
                  key={m}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs cursor-pointer ${
                    form.capitalizationMode === m ? 'border-indigo-300 bg-indigo-50/50 text-[#1e293b] font-semibold' : 'border-[#e2e8f0] text-slate-600 hover:bg-slate-50'
                  } ${financialLocked ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  <input type="radio" checked={form.capitalizationMode === m} onChange={() => set({ capitalizationMode: m })} />
                  {MODE_LABEL[m]}
                </label>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label={isOpening ? 'Originally Purchased On' : 'Purchase Date'} required>
                <input type="date" value={form.purchaseDate} max={todayStr()} disabled={financialLocked} onChange={(e) => set({ purchaseDate: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Put to Use On" hint="Depreciation starts from this date">
                <input type="date" value={form.depreciationStartDate} min={form.purchaseDate} disabled={financialLocked} onChange={(e) => set({ depreciationStartDate: e.target.value })} placeholder={form.purchaseDate} className={inputCls} />
              </Field>
              {['BANK', 'CASH'].includes(form.capitalizationMode) && (
                <Field label={form.capitalizationMode === 'CASH' ? 'Paid From (Cash)' : 'Paid From (Bank)'}>
                  <select value={form.paymentAccountId} disabled={financialLocked} onChange={(e) => set({ paymentAccountId: e.target.value })} className={inputCls}>
                    <option value="">{form.capitalizationMode === 'CASH' ? 'Cash in Hand' : 'Default bank account'}</option>
                    {bankLedgers.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {isInvoice ? (
                <Field label="Purchase Invoice" required hint="Non-stock bill from Purchase → Invoices (its GST and vendor payable are already posted)">
                  <select value={form.purchaseInvoiceId} disabled={financialLocked} onChange={(e) => {
                    const inv = invoices.find((i) => i.id === e.target.value);
                    set({ purchaseInvoiceId: e.target.value, purchaseCost: inv ? inv.taxable_amount : form.purchaseCost });
                  }} className={inputCls}>
                    <option value="">Select bill…</option>
                    {invoices.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.invoice_number} · {i.supplier_name || ''} · {money(i.taxable_amount)}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                !isOpening && (
                  <Field label={form.capitalizationMode === 'VENDOR_CREDIT' ? 'Vendor' : 'Vendor (optional)'} required={form.capitalizationMode === 'VENDOR_CREDIT'}>
                    <select value={form.vendorId} disabled={financialLocked} onChange={(e) => set({ vendorId: e.target.value })} className={inputCls}>
                      <option value="">—</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                )
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Purchase Cost (excl. GST)" required>
                <input type="number" min={0} value={form.purchaseCost || ''} disabled={financialLocked} onChange={(e) => set({ purchaseCost: Number(e.target.value) })} className={inputCls} />
              </Field>
              <Field label="Freight / Installation / Trial Run" hint="Capitalised with the asset">
                <input type="number" min={0} value={form.additionalCost || ''} disabled={financialLocked} onChange={(e) => set({ additionalCost: Number(e.target.value) })} className={inputCls} />
              </Field>
              <div className="bg-slate-50 border border-[#e2e8f0] rounded-lg px-3 py-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Capitalised Cost</p>
                <p className="text-lg font-bold text-[#1e293b]">{money(capitalized)}</p>
              </div>
            </div>

            {!isOpening && !isInvoice && (
              <div className={financialLocked ? 'opacity-60 pointer-events-none' : ''}>
                <GstFields
                  gstType={form.gstType}
                  gstAmount={form.gstAmount}
                  itcEligible={form.itcEligible}
                  base={Number(form.purchaseCost || 0) + Number(form.additionalCost || 0)}
                  onChange={(p) => set(p)}
                />
                {gst > 0 && !form.itcEligible && <p className="text-[11px] text-amber-700 mt-1">Without ITC the GST becomes part of the asset cost.</p>}
              </div>
            )}

            {isOpening && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Accumulated Depreciation till Date" hint="Depreciation already charged before this system">
                  <input type="number" min={0} value={form.openingAccumulatedDepreciation || ''} disabled={financialLocked} onChange={(e) => set({ openingAccumulatedDepreciation: Number(e.target.value) })} className={inputCls} />
                </Field>
                <Field label="Depreciation Charged Up To" required={form.openingAccumulatedDepreciation > 0}>
                  <input type="date" value={form.openingDepreciationUpto} max={todayStr()} disabled={financialLocked} onChange={(e) => set({ openingDepreciationUpto: e.target.value })} className={inputCls} />
                </Field>
              </div>
            )}

            <EntryPreview lines={entry} />
          </div>
        </Section>

        <Section title="Book Depreciation" subtitle="Posted monthly to the General Ledger. Tax (IT Act) depreciation is calculated separately in Reports.">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Method">
              <select value={form.depreciationMethod} disabled={financialLocked} onChange={(e) => set({ depreciationMethod: e.target.value as DepreciationMethod })} className={inputCls}>
                <option value="SLM">Straight Line (SLM)</option>
                <option value="WDV">Written Down Value (WDV)</option>
                <option value="NONE">Not depreciated (land, goodwill)</option>
              </select>
            </Field>
            {form.depreciationMethod === 'WDV' && (
              <Field label="WDV Rate (% per year)">
                <input type="number" min={0} max={100} value={form.depreciationRate || ''} disabled={financialLocked} onChange={(e) => set({ depreciationRate: Number(e.target.value) })} className={inputCls} />
              </Field>
            )}
            {form.depreciationMethod !== 'NONE' && (
              <Field label="Useful Life (years)" hint={form.depreciationMethod === 'WDV' ? 'For reference' : undefined}>
                <input type="number" min={0} value={form.usefulLifeYears} disabled={financialLocked} onChange={(e) => set({ usefulLifeYears: e.target.value === '' ? '' : Number(e.target.value) })} className={inputCls} />
              </Field>
            )}
            {form.depreciationMethod !== 'NONE' && (
              <Field label="Salvage Value" hint="Expected value at end of life">
                <input type="number" min={0} value={form.scrapValue || ''} disabled={financialLocked} onChange={(e) => set({ scrapValue: Number(e.target.value) })} className={inputCls} />
              </Field>
            )}
          </div>
          {schedule.length > 0 && (
            <div className="mt-4 border border-[#e2e8f0] rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className={`${'px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider'} text-left`}>Year</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right">Opening</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right">Depreciation</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right">Closing</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {schedule.map((r) => (
                    <tr key={r.year}>
                      <td className="px-3 py-1.5 text-slate-600">Year {r.year}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{money(r.opening)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-red-600">{money(r.dep)}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">{money(r.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-400 px-3 py-1.5">Indicative full years; the monthly run charges pro-rata from the put-to-use date.</p>
            </div>
          )}
        </Section>

        <Section title="Location & Custody">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Warehouse / Site">
              <select value={form.warehouseId} onChange={(e) => set({ warehouseId: e.target.value })} className={inputCls}>
                <option value="">—</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Department">
              <input value={form.department} onChange={(e) => set({ department: e.target.value })} placeholder="e.g. Manufacturing" className={inputCls} />
            </Field>
            <Field label="Location">
              <input value={form.locationDetail} onChange={(e) => set({ locationDetail: e.target.value })} placeholder="e.g. Factory Floor - Section B" className={inputCls} />
            </Field>
            <Field label="Custodian">
              <input value={form.custodianName} onChange={(e) => set({ custodianName: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Production Machine" hint="Links the asset to the machine used in work orders">
              <select value={form.machineId} onChange={(e) => set({ machineId: e.target.value })} className={inputCls}>
                <option value="">— Not a production machine —</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id} disabled={!!m.linked_asset_code && m.id !== form.machineId}>
                    {m.name}{m.model ? ` (${m.model})` : ''}{m.linked_asset_code && m.id !== form.machineId ? ` — linked to ${m.linked_asset_code}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Warranty & Insurance">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Field label="Warranty Till">
              <input type="date" value={form.warrantyExpiry} onChange={(e) => set({ warrantyExpiry: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Insurance Company">
              <input value={form.insuranceCompany} onChange={(e) => set({ insuranceCompany: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Policy No.">
              <input value={form.insurancePolicyNo} onChange={(e) => set({ insurancePolicyNo: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Insured Value">
              <input type="number" min={0} value={form.insuredValue || ''} onChange={(e) => set({ insuredValue: Number(e.target.value) })} className={inputCls} />
            </Field>
            <Field label="Insurance Expiry">
              <input type="date" value={form.insuranceExpiry} onChange={(e) => set({ insuranceExpiry: e.target.value })} className={inputCls} />
            </Field>
          </div>
        </Section>

        <div className="flex items-center justify-end gap-3 pb-6">
          <button onClick={() => navigate(editId ? `/finance/assets/${editId}` : '/finance/assets')} className={secondaryBtn}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className={primaryBtn}>
            {saving ? 'Saving…' : editId ? 'Save Changes' : 'Register Asset & Post Entry'}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
